import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { HomeFooter } from '../components/home/HomeFooter';
import { HomeHeader } from '../components/home/HomeHeader';
import { useGuestGate } from '../components/home/GuestGateProvider';
import { loadAuthSession } from '../data/authSession';

import {
  createBand,
  getBand,
  updateBand,
  uploadBandImage,
  GENRE_LABELS,
  REGION_LABELS,
  type Genre,
  type Region,
} from '../api/bandworld';

const REGION_OPTIONS = Object.entries(REGION_LABELS) as [Region, string][];
const GENRE_OPTIONS = Object.entries(GENRE_LABELS) as [Genre, string][];

export function BandFormPage() {
  const navigate = useNavigate();
  const { bandId } = useParams<{ bandId: string }>();
  const isEdit = Boolean(bandId);
  const isAuthenticated = Boolean(loadAuthSession());
  const { openGuestGate } = useGuestGate();

  // form fields
  const [name, setName] = useState('');
  const [introduction, setIntroduction] = useState('');
  const [genre, setGenre] = useState<Genre | ''>('');
  const [region, setRegion] = useState<Region | ''>('');
  const [capacity, setCapacity] = useState<number>(4);

  // image
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ui state
  const [loadingInitial, setLoadingInitial] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // load existing band for edit mode
  useEffect(() => {
    if (!isEdit || !bandId) return;
    let cancelled = false;
    setLoadingInitial(true);
    setLoadError(null);

    getBand(bandId)
      .then((band) => {
        if (cancelled) return;
        setName(band.name);
        setIntroduction(band.introduction ?? '');
        setGenre(band.genre ?? '');
        setRegion(band.region ?? '');
        setCapacity(band.capacity);
        setExistingImageUrl(band.imageUrl ?? null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setLoadError(err.message ?? '밴드 정보를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoadingInitial(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isEdit, bandId]);

  // revoke preview object URL on unmount / file change
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
    setExistingImageUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canSubmit = name.trim().length > 0 && capacity >= 1;

  const handleSubmit = async () => {
    if (!canSubmit || submitLoading) return;
    if (!isAuthenticated) {
      openGuestGate();
      return;
    }
    setSubmitLoading(true);
    setSubmitError('');

    try {
      let imageUrl: string | undefined;

      // Upload new image if selected
      if (selectedFile) {
        imageUrl = await uploadBandImage(selectedFile);
      } else if (existingImageUrl) {
        // preserve existing image in edit mode
        imageUrl = existingImageUrl;
      }

      if (isEdit && bandId) {
        const result = await updateBand(bandId, {
          name: name.trim(),
          introduction: introduction.trim() || undefined,
          genre: genre || undefined,
          region: region || undefined,
          capacity,
          imageUrl,
        });
        navigate(`/bands/${result.bandId}`);
      } else {
        const result = await createBand({
          name: name.trim(),
          introduction: introduction.trim() || undefined,
          genre: genre || undefined,
          region: region || undefined,
          capacity,
          imageUrl,
        });
        navigate(`/bands/${result.bandId}`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError(isEdit ? '밴드 수정에 실패했습니다.' : '밴드 생성에 실패했습니다.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loadingInitial) {
    return (
      <main className="band-form-page">
        <HomeHeader authenticated={isAuthenticated} onGuestCta={() => navigate('/login')} variant="icon" />
        <section className="band-form-page__inner">
          <div className="band-form-page__loading">로딩 중...</div>
        </section>
        <HomeFooter />
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="band-form-page">
        <HomeHeader authenticated={isAuthenticated} onGuestCta={() => navigate('/login')} variant="icon" />
        <section className="band-form-page__inner">
          <div className="band-form-page__error">{loadError}</div>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{ minHeight: '44px', padding: '0.5rem 1.5rem', borderRadius: '8px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
            >
              뒤로 가기
            </button>
          </div>
        </section>
        <HomeFooter />
      </main>
    );
  }

  const displayImage = previewUrl ?? existingImageUrl;

  return (
    <main className="band-form-page">
      <HomeHeader authenticated={isAuthenticated} onGuestCta={() => navigate('/login')} variant="icon" />

      <section className="band-form-page__inner">
        <button className="band-form-page__back" type="button" onClick={() => navigate(-1)}>
          ← 뒤로
        </button>

        <h1 className="band-form-page__title">
          {isEdit ? '밴드 수정' : '밴드 만들기'}
        </h1>

        {/* 대표 이미지 */}
        <div className="band-form-page__image-section">
          <div className="band-form-page__image-preview">
            {displayImage ? (
              <img
                className="band-form-page__image-thumb"
                src={displayImage}
                alt="밴드 대표 이미지 미리보기"
              />
            ) : (
              <div className="band-form-page__image-placeholder" aria-hidden="true" />
            )}
          </div>
          <div className="band-form-page__image-actions">
            <button
              type="button"
              className="band-form-page__image-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              {displayImage ? '이미지 변경' : '이미지 선택'}
            </button>
            {displayImage ? (
              <button
                type="button"
                className="band-form-page__image-btn band-form-page__image-btn--remove"
                onClick={handleRemoveImage}
              >
                이미지 제거
              </button>
            ) : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>

        {/* 밴드 이름 */}
        <div className="band-form-page__field">
          <label className="band-form-page__label" htmlFor="band-name">
            밴드 이름 <span className="band-form-page__required">*</span>
          </label>
          <input
            id="band-name"
            type="text"
            className="band-form-page__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            placeholder="밴드 이름을 입력하세요"
          />
        </div>

        {/* 소개 */}
        <div className="band-form-page__field">
          <label className="band-form-page__label" htmlFor="band-introduction">
            밴드 소개
          </label>
          <textarea
            id="band-introduction"
            className="band-form-page__textarea"
            value={introduction}
            onChange={(e) => setIntroduction(e.target.value)}
            maxLength={500}
            placeholder="밴드를 소개해주세요"
            rows={4}
          />
        </div>

        {/* 장르 */}
        <div className="band-form-page__field">
          <label className="band-form-page__label" htmlFor="band-genre">
            장르
          </label>
          <select
            id="band-genre"
            className="band-form-page__select"
            value={genre}
            onChange={(e) => setGenre(e.target.value as Genre | '')}
          >
            <option value="">장르 선택 (선택사항)</option>
            {GENRE_OPTIONS.map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>

        {/* 활동 지역 */}
        <div className="band-form-page__field">
          <label className="band-form-page__label" htmlFor="band-region">
            활동 지역
          </label>
          <select
            id="band-region"
            className="band-form-page__select"
            value={region}
            onChange={(e) => setRegion(e.target.value as Region | '')}
          >
            <option value="">지역 선택 (선택사항)</option>
            {REGION_OPTIONS.map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>

        {/* 모집 인원 */}
        <div className="band-form-page__field">
          <label className="band-form-page__label" htmlFor="band-capacity">
            모집 인원 <span className="band-form-page__required">*</span>
          </label>
          <input
            id="band-capacity"
            type="number"
            className="band-form-page__input band-form-page__input--short"
            value={capacity}
            onChange={(e) => setCapacity(Math.max(1, Number(e.target.value)))}
            min={1}
            max={99}
          />
        </div>

        {submitError ? (
          <div className="band-form-page__submit-error">{submitError}</div>
        ) : null}

        <button
          type="button"
          className={`band-form-page__submit${canSubmit && !submitLoading ? ' band-form-page__submit--active' : ''}`}
          disabled={!canSubmit || submitLoading}
          onClick={handleSubmit}
        >
          {submitLoading ? '저장 중...' : isEdit ? '수정 완료' : '밴드 만들기'}
        </button>
      </section>

      <HomeFooter />
    </main>
  );
}
